import { Badge } from "@thilke/ui/components/badge";
import { Button } from "@thilke/ui/components/button";
import { Input } from "@thilke/ui/components/input";
import { ScrollArea } from "@thilke/ui/components/scroll-area";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@thilke/ui/components/select";
import { Separator } from "@thilke/ui/components/separator";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@thilke/ui/components/ui/collapsible";
import { cn } from "@thilke/ui/lib/utils";
import {
  Box,
  ChevronRight,
  Component,
  FileJson,
  FolderTree,
  Search,
} from "lucide-react";
import type * as React from "react";
import { type RegistryTheme, registryThemes } from "../theme-options";

type RegistryItem = {
  name: string;
  title?: string;
  type: string;
  categories?: string[];
};

// Display order for component categories in the sidebar tree.
const CATEGORY_ORDER = [
  "primitives",
  "form",
  "overlay",
  "navigation",
  "layout",
  "data-display",
  "feedback",
  "media",
  "code",
  "marketing",
  "game-tabletop",
  "foundry",
  "hooks",
  "styles",
];

function primaryCategory(item: RegistryItem): string {
  return item.categories?.[0] ?? "other";
}

function categoryLabel(category: string): string {
  return category
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
type AppSidebarProps = {
  origin: string;
  registryName: string;
  items: RegistryItem[];
  filteredItems: RegistryItem[];
  query: string;
  onQueryChange: (value: string) => void;
  theme: RegistryTheme;
  onThemeChange: (theme: RegistryTheme) => void;
};
function titleFor(item: RegistryItem) {
  return (
    item.title ??
    item.name.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase())
  );
}
function sortItems(items: RegistryItem[]) {
  return [...items].sort((a, b) => titleFor(a).localeCompare(titleFor(b)));
}
function TreeSection({
  title,
  icon,
  items,
  defaultOpen,
}: {
  title: string;
  icon: React.ReactNode;
  items: RegistryItem[];
  defaultOpen?: boolean;
}) {
  return (
    <Collapsible defaultOpen={defaultOpen}>
      <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-foreground hover:bg-accent hover:text-accent-foreground">
        <ChevronRight className="size-4 transition-transform group-data-[state=open]:rotate-90" />
        {icon}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        <span className="font-mono text-xs text-muted-foreground">
          {items.length}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-1 grid gap-1 pl-4">
          {sortItems(items).map((item) => (
            <a
              key={item.name}
              href={`#${item.name}`}
              className={cn(
                "flex min-h-7 items-center rounded-md px-2 py-1 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground",
              )}
            >
              <span className="min-w-0 flex-1 truncate">{titleFor(item)}</span>
            </a>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
export function AppSidebar({
  origin,
  registryName,
  items,
  filteredItems,
  query,
  onQueryChange,
  theme,
  onThemeChange,
}: AppSidebarProps) {
  const components = filteredItems.filter(
    (item) => item.type !== "registry:block",
  );
  const blocks = filteredItems.filter((item) => item.type === "registry:block");

  // Group components by their primary category for discoverability.
  const componentsByCategory = new Map<string, RegistryItem[]>();
  for (const item of components) {
    const category = primaryCategory(item);
    const bucket = componentsByCategory.get(category);
    if (bucket) bucket.push(item);
    else componentsByCategory.set(category, [item]);
  }
  const categoryGroups = [...componentsByCategory.entries()].sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a[0]);
    const bi = CATEGORY_ORDER.indexOf(b[0]);
    if (ai === -1 && bi === -1)
      return categoryLabel(a[0]).localeCompare(categoryLabel(b[0]));
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  return (
    <aside className="sticky top-0 hidden h-dvh w-80 shrink-0 border-r bg-card/60 lg:flex lg:flex-col">
      <div className="border-b p-4">
        <div className="flex items-center gap-3">
          <div className="flex size-9 items-center justify-center rounded-md border bg-background">
            <Component className="size-4" />
          </div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold">Thilke UI</div>
            <div className="truncate text-xs text-muted-foreground">
              Component registry
            </div>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Badge variant="outline" className="justify-center">
            {registryName}
          </Badge>
          <Badge variant="secondary" className="justify-center">
            {items.length} items
          </Badge>
        </div>
      </div>
      <div className="grid gap-3 border-b p-4">
        <label htmlFor="component-search" className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id="component-search"
            value={query}
            onChange={(event) => onQueryChange(event.target.value)}
            placeholder="Search components..."
            className="pl-8"
          />
        </label>
        <div className="grid gap-1.5">
          <label
            htmlFor="registry-theme"
            className="text-xs text-muted-foreground"
          >
            Theme
          </label>
          <Select
            value={theme}
            onValueChange={(value) => onThemeChange(value as RegistryTheme)}
          >
            <SelectTrigger id="registry-theme" size="sm">
              <SelectValue aria-label="Registry theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                {registryThemes.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
      </div>
      <ScrollArea className="min-h-0 flex-1">
        <div className="grid gap-1 p-3">
          <div className="mb-1 flex items-center gap-2 px-2 text-xs font-medium text-muted-foreground">
            <FolderTree className="size-4" /> Library
          </div>
          {categoryGroups.map(([category, categoryItems], index) => (
            <TreeSection
              key={category}
              title={categoryLabel(category)}
              icon={<Component className="size-4" />}
              items={categoryItems}
              defaultOpen={index < 3}
            />
          ))}
          <TreeSection
            title="Blocks"
            icon={<Box className="size-4" />}
            items={blocks}
            defaultOpen
          />
        </div>
      </ScrollArea>
      <div className="border-t p-3">
        <Button variant="outline" asChild className="w-full justify-start">
          <a href={`${origin}/registry.json`} target="_blank" rel="noreferrer">
            <FileJson className="size-4" /> Registry JSON
          </a>
        </Button>
        <Separator className="my-3" />
        <div className="grid grid-cols-2 gap-1 text-center font-mono text-xs text-muted-foreground">
          <div className="rounded-md border bg-background/60 px-1 py-1">
            {components.length}
          </div>
          <div className="rounded-md border bg-background/60 px-1 py-1">
            {blocks.length}
          </div>
        </div>
      </div>
    </aside>
  );
}
